import { createElement, FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Divider, Modal, Row, Typography } from 'antd';
import axios from 'axios';
import { TbArrowRight, TbInfoCircle, TbPuzzle, TbRosetteDiscount } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { FeatureAddOnConfig } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useBillingSnapshot } from '@/hooks/useBillingSnapshot';
import { OurDocsLink, PlatformAlert, SmallTag, SmallTooltip } from '@/shared';
import { ButtonSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useBillingStore } from '@/stores/database/aliases/billing';
import { SetStateMethod } from '@/types';
import { dayjs } from '@/utilities';

interface EnableFeatureAddOnModalProps {
    featureAddOn: FeatureAddOnConfig;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const EnableFeatureAddOnModal: FC<EnableFeatureAddOnModalProps> = ({ open, setOpen, featureAddOn }): JSX.Element => {
    const { refreshSnapshot } = useBillingStore();
    const { snapshot } = useBillingSnapshot();

    const { token } = useAntDesignTheme();

    const [preview, setPreview] = useState<null | {
        billingInterval: 'monthly' | 'annual';
        amountDueToday: number;
        nextInvoiceTotal: number;
        nextInvoiceDate: string;
        currency: string;
    }>(null);

    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }

        const controller = new AbortController();

        const fetchPreview = async () => {
            try {
                setPreviewLoading(true);

                const res = await authorizedAxiosInstance.get(`/api/billing/feature-add-ons/preview`, {
                    params: { featureKey: featureAddOn.key },
                    signal: controller.signal,
                });

                // console.log(res?.data);

                setPreview(res.data);
            } catch (err) {
                if (!axios.isCancel(err)) {
                    setPreviewError(true);
                }
            } finally {
                setPreviewLoading(false);
            }
        };

        fetchPreview();

        return () => controller.abort();
    }, [open, featureAddOn.key]);

    const [confirmLoading, setConfirmLoading] = useState(false);

    const handleEnableFeatureAddOn = async () => {
        try {
            setConfirmLoading(true);

            await authorizedAxiosInstance.post(`/api/billing/feature-add-ons/enable`, { featureKey: featureAddOn.key });

            setOpen(false);

            toast('Add-On Enabled', {
                icon: <TbPuzzle size={'1rem'} />,
                description: featureAddOn.label,
            });

            // !IMPORTANT:
            await refreshSnapshot();
        } catch (err) {
            captureException(err);

            toast.error('Unable to enable add-on. Please try again.');
        } finally {
            setConfirmLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={() => setOpen(false)}
            title={<>Enable Add-On</>}
            footer={
                <Row>
                    <Col flex='none'>
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                    </Col>
                    <Col flex='auto'></Col>
                    <Col flex='none'>
                        <Button
                            type='primary'
                            loading={previewLoading || confirmLoading}
                            disabled={!preview || previewError || previewLoading || confirmLoading}
                            onClick={handleEnableFeatureAddOn}
                        >
                            Confirm & Enable Add-On
                            <TbArrowRight />
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Typography.Title level={5} style={{ paddingBottom: 0, marginTop: '0.5rem' }}>
                <Avatar
                    size={24}
                    shape='square'
                    icon={createElement(featureAddOn?.icon, {
                        style: { color: token.colorTextTertiary },
                        size: '67.5%',
                    })}
                    style={{
                        border: `1px solid ${token.colorBorder}`,
                        marginRight: '0.5rem',
                        verticalAlign: '-0.5px',
                    }}
                />
                {featureAddOn?.label}
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                {featureAddOn.description}
            </Typography.Paragraph>
            {/* <OurDocsLink text={'Demo Video'} href={''} /> */}
            <Row style={{ marginTop: '0.5rem' }}>
                <Col flex='none'>
                    <div>
                        <small style={{ opacity: '50%', fontWeight: 400 }}>
                            $<strong>{featureAddOn?.pricing?.monthly?.unitAmount}</strong>/active seat/month or $
                            <strong>{featureAddOn?.pricing?.annual?.yearlyAmount}</strong>
                            /active seat/year
                        </small>
                    </div>
                </Col>
                <Col flex='auto'></Col>
                <Col flex='none'>
                    <SmallTag
                        message={
                            <>
                                <TbRosetteDiscount style={{ verticalAlign: '-0.1rem', marginRight: '0.125rem' }} />
                                20% Off Annual Plans
                            </>
                        }
                        styles={{ backgroundColor: token.green2, color: token.green7 }}
                    />
                </Col>
            </Row>
            <Divider style={{ margin: '1rem 0' }} />
            <div style={{ marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Active Seats in Workspace</small>
            </div>
            <div style={{ marginBottom: '0.25rem' }}>{snapshot?.seats?.activeSeats}</div>
            <div style={{ marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Billing Cycle</small>
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
                <span style={{ marginRight: '0.5rem' }}>
                    {snapshot?.subscription?.billingCycle === 'month' ? 'Monthly' : 'Annual'}
                </span>
                <SmallTooltip
                    title={
                        <>
                            Your billing cycle for feature add-ons is automatically determined by the billing cycle for
                            Seats. Learn more about how we handle billing in <OurDocsLink />
                        </>
                    }
                    placement='topLeft'
                    padding='0.25rem 0'
                >
                    <span>
                        <SmallTag
                            message={
                                <span style={{ color: token.colorTextSecondary }}>
                                    <TbInfoCircle style={{ verticalAlign: '-1.25px', marginRight: '0.25rem' }} />
                                    Determined by Seat Billing Cycle
                                </span>
                            }
                            styles={{ cursor: 'help', marginLeft: 0 }}
                        />
                    </span>
                </SmallTooltip>
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Price per Active Seat for this Feature Add-On</small>
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
                $
                <strong>
                    {snapshot?.subscription?.billingCycle === 'month'
                        ? featureAddOn?.pricing?.monthly?.unitAmount
                        : featureAddOn?.pricing?.annual?.yearlyAmount}
                </strong>
                /active seat/
                {snapshot?.subscription?.billingCycle}
            </div>
            <Divider style={{ margin: '1rem 0' }} />
            {/* <div style={{ marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Charged Today (Prorated)</small>
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
                {previewLoading ? (
                    <ButtonSkeleton size='small' style={{ width: 16 * 16 }} active />
                ) : preview ? (
                    <div>${(preview.amountDueToday / 100).toFixed(2)}</div>
                ) : (
                    <Typography.Text type='danger'>
                        Unable to calculate charges. Please refresh & try again.
                    </Typography.Text>
                )}
            </div> */}
            <div style={{ marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Next Invoice Date</small>
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
                {previewLoading ? (
                    <ButtonSkeleton size='small' style={{ width: 16 * 16 }} active />
                ) : preview ? (
                    <div>{dayjs(preview?.nextInvoiceDate as any).format('LL')}</div>
                ) : (
                    <Typography.Text type='danger'>
                        Unable to calculate charges. Please refresh & try again.
                    </Typography.Text>
                )}
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Next Invoice Total, Including This Add-On</small>
            </div>
            <div style={{ marginBottom: '0.25rem' }}>
                {previewLoading ? (
                    <ButtonSkeleton size='small' style={{ width: 16 * 16 }} active />
                ) : preview ? (
                    <div>
                        ${Number(((preview?.nextInvoiceTotal as any) / 100).toFixed(2)).toLocaleString()} /{' '}
                        {preview?.billingInterval}
                    </div>
                ) : (
                    <Typography.Text type='danger'>
                        Unable to calculate charges. Please refresh & try again.
                    </Typography.Text>
                )}
            </div>
            <Divider style={{ margin: '1rem 0' }} />
            <PlatformAlert
                status='info'
                title='Prorated billing'
                // message={<></>}
                description={
                    <>
                        You’ll be charged a prorated amount today for the remainder of your billing period. Future
                        invoices will include the full price based on your billing cycle.
                    </>
                }
            />
        </Modal>
    );
};

export default EnableFeatureAddOnModal;
